import React, { useState, useMemo } from 'react';
import { Gamepad2 } from 'lucide-react';
import { createAvatar } from '@dicebear/core';
import { avataaars } from '@dicebear/collection';
import { STAT_LABELS } from './constants';
import RadarChart from './RadarChart';

const AVATAR_PRESETS = [
  { seed: 'pol-1', alias: 'O Reformista', stats: [18, 20, 15, 8], clothing: ['blazerAndShirt'], top: ['shortFlat'], skinColor: ['f2d3b1'], facialHair: [], facialHairProbability: 0, accessories: [], accessoriesProbability: 0, eyes: ['default'], mouth: ['smile'], eyebrows: ['defaultNatural'] },
  { seed: 'pol-2', alias: 'A Tecnocrata', stats: [10, 25, 22, 5], clothing: ['blazerAndShirt'], top: ['straight01'], skinColor: ['f2d3b1'], facialHair: [], facialHairProbability: 0, accessories: ['prescription01'], accessoriesProbability: 100, eyes: ['happy'], mouth: ['twinkle'], eyebrows: ['raisedExcitedNatural'] },
  { seed: 'pol-3', alias: 'O Populista', stats: [28, 12, 8, 18], clothing: ['blazerAndSweater'], top: ['shortWaved'], skinColor: ['c68642'], facialHair: ['beardLight'], facialHairProbability: 100, accessories: [], accessoriesProbability: 0, eyes: ['default'], mouth: ['serious'], eyebrows: ['defaultNatural'] },
  { seed: 'pol-4', alias: 'A Conciliadora', stats: [22, 15, 12, 6], clothing: ['blazerAndShirt'], top: ['bob'], skinColor: ['8d5524'], facialHair: [], facialHairProbability: 0, accessories: [], accessoriesProbability: 0, eyes: ['happy'], mouth: ['smile'], eyebrows: ['flatNatural'] },
  { seed: 'pol-5', alias: 'O Oligarca', stats: [8, 10, 28, 25], clothing: ['collarAndSweater'], top: ['theCaesar'], skinColor: ['e0ac69'], facialHair: ['moustacheFancy'], facialHairProbability: 100, accessories: ['prescription02'], accessoriesProbability: 100, eyes: ['squint'], mouth: ['default'], eyebrows: ['upDownNatural'] },
  { seed: 'pol-6', alias: 'A Influencer', stats: [30, 18, 5, 10], clothing: ['blazerAndShirt'], top: ['longButNotTooLong'], skinColor: ['e0ac69'], facialHair: [], facialHairProbability: 0, accessories: [], accessoriesProbability: 0, eyes: ['default'], mouth: ['twinkle'], eyebrows: ['raisedExcitedNatural'] },
  { seed: 'pol-7', alias: 'O Sindicalista', stats: [20, 22, 10, 12], clothing: ['blazerAndSweater'], top: ['shortCurly'], skinColor: ['614335'], facialHair: ['beardMedium'], facialHairProbability: 100, accessories: [], accessoriesProbability: 0, eyes: ['side'], mouth: ['smile'], eyebrows: ['defaultNatural'] },
  { seed: 'pol-8', alias: 'A Diplomata', stats: [16, 20, 14, 3], clothing: ['blazerAndShirt'], top: ['bun'], skinColor: ['ffdbac'], facialHair: [], facialHairProbability: 0, accessories: ['round'], accessoriesProbability: 100, eyes: ['happy'], mouth: ['smile'], eyebrows: ['flatNatural'] },
];

export function generateAvatarSvg(opts) {
  return createAvatar(avataaars, {
    ...opts,
    clothesColor: ['0b2a3a'],
    style: ['circle'],
    backgroundColor: ['1a1a2e'],
  }).toString();
}

export default function AvatarSelector({ onSelect }) {
  const [selected, setSelected] = useState(null);
  const avatars = useMemo(() =>
    AVATAR_PRESETS.map((opts) => ({
      ...opts,
      svg: generateAvatarSvg(opts),
    })),
  []);

  const sel = selected !== null ? avatars[selected] : null;

  return (
    <div className="avatar-selector">
      <Gamepad2 size={40} className="avatar-selector__icon" />
      <h2 className="avatar-selector__title">Escolha seu Perfil</h2>
      <p className="avatar-selector__subtitle">Cada político tem suas forças — e fraquezas.</p>

      <div className="avatar-selector__grid">
        {avatars.map((av, i) => {
          const isSelected = selected === i;
          return (
            <button
              key={av.seed}
              onClick={() => setSelected(i)}
              className="avatar-selector__btn group"
              style={{
                borderRadius: 16,
                padding: '14px 8px 10px',
                background: isSelected ? 'rgba(212,165,116,.08)' : 'rgba(255,255,255,.03)',
                border: isSelected ? '2px solid rgba(212,165,116,.7)' : '1px solid rgba(255,255,255,.07)',
                cursor: 'pointer',
                boxShadow: isSelected ? '0 0 0 3px rgba(212,165,116,.10)' : 'none',
                transform: isSelected ? 'scale(1.03)' : 'scale(1)',
                transition: 'all 0.2s',
              }}
            >
              <div className="avatar-selector__avatar-circle" dangerouslySetInnerHTML={{ __html: av.svg }} />
              <span className="avatar-selector__name"
                style={{ color: isSelected ? 'rgba(212,165,116,.95)' : 'rgba(255,255,255,.6)' }}>
                {av.alias}
              </span>
            </button>
          );
        })}
      </div>

      {/* Detail panel */}
      <div className={`avatar-detail ${sel ? 'avatar-detail--active' : 'avatar-detail--empty'}`}>
        {sel ? (
          <div className="avatar-detail__inner">
            <div className="avatar-detail__row">
              <div className="avatar-detail__portrait" dangerouslySetInnerHTML={{ __html: sel.svg }} />
              <div>
                <h3 className="avatar-detail__name">{sel.alias}</h3>
                <div className="avatar-detail__stats">
                  {STAT_LABELS.map((label, i) => (
                    <span key={label} className="avatar-detail__stat-badge"
                      style={{
                        background: sel.stats[i] >= 20 ? 'rgba(52,211,153,.12)' : sel.stats[i] >= 10 ? 'rgba(251,191,36,.10)' : 'rgba(239,68,68,.10)',
                        color: sel.stats[i] >= 20 ? '#6ee7b7' : sel.stats[i] >= 10 ? '#fbbf24' : '#f87171',
                        border: `1px solid ${sel.stats[i] >= 20 ? 'rgba(52,211,153,.2)' : sel.stats[i] >= 10 ? 'rgba(251,191,36,.15)' : 'rgba(239,68,68,.15)'}`,
                      }}>
                      {label}: {sel.stats[i]}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="avatar-detail__radar">
              <RadarChart stats={sel.stats} size={160} />
            </div>
          </div>
        ) : (
          <div className="avatar-detail__empty">Selecione um perfil acima</div>
        )}
      </div>

      <button
        disabled={selected === null}
        onClick={() => onSelect(sel)}
        className={`avatar-selector__confirm ${selected !== null ? 'avatar-selector__confirm--active' : 'avatar-selector__confirm--disabled'}`}
      >
        {sel ? `Jogar como ${sel.alias}` : 'Confirmar Perfil'}
      </button>
    </div>
  );
}
